import type { AutoKeyframe, EaseType, EaseTypeImpl, Keyframe, Lerpable } from './types';

import { makeArcLengthMap, velocityToBezierEase } from '@use-gpu/core';

import { EaseNumber } from './ease/number';
import { EaseAngle } from './ease/angle';
import { EaseQuat } from './ease/quat';
import { EaseMat4 } from './ease/mat4';

import { clone } from './ease/zip';

export { clone as cloneLerpable, copy as copyLerpable } from './ease/zip';

export const EaseTypes: Record<EaseType, EaseTypeImpl<any>> = {
  number: EaseNumber,
  angle: EaseAngle,
  quat: EaseQuat,
  mat4: EaseMat4,
};

export const automaticKeyframes = <T extends Lerpable>(
  keyframes: AutoKeyframe<T>[],
  easeType: EaseType = 'number',
  loop?: boolean,
): Keyframe<T>[] => {

  const et = EaseTypes[easeType];

  const intervals: { duration: number, knots: T[] }[] = [];
  const n = keyframes.length;

  for (let i = 0; i < n - 1; ++i) {
    const {time: start, knots: ks} = keyframes[i];
    const {time: end} = keyframes[i + 1];
    const duration = end - start;

    if (ks) {
      intervals.push({ duration, knots: ks });
    }
    else {
      const knots = et.auto(
        (keyframes[i - 1] ?? (loop ? keyframes[n - 2] : keyframes[i])).value,
        keyframes[i].value,
        keyframes[i + 1].value,
        (keyframes[i + 2] ?? (loop ? keyframes[1] : keyframes[i + 1])).value,
      );

      intervals.push({ duration, knots });
    }
  }

  const v1 = clone(keyframes[0].value);
  const v2 = clone(keyframes[0].value);

  const arcLengths = intervals.map((interval, i) => {
    const from = keyframes[i].value;
    const to = keyframes[i + 1].value;

    const {knots} = interval;
    const spline = et.spline(from, to, knots);

    const measure = (t1: number, t2: number) => {
      spline(v1, t1);
      spline(v2, t2);
      return et.measure(v1, v2);
    };

    const map = makeArcLengthMap(measure);
    return map.length;
  });

  const speeds = keyframes.map((keyframe, i) => {
    if (keyframe.speed != null) return keyframe.speed;

    const beforeIndex = loop && i === 0 ? n - 2 : i - 1;
    const afterIndex = loop && i === n - 1 ? 0 : i;

    const dT = (intervals[beforeIndex]?.duration ?? 0) + (intervals[afterIndex]?.duration ?? 0);
    const dV = (arcLengths[beforeIndex] ?? 0) + (arcLengths[afterIndex] ?? 0);

    return dT ? dV / dT : 0;
  });

  const mapped = intervals.map((interval, i) => {
    const {duration, knots} = interval;
    const arcLength = arcLengths[i];

    const f1 = keyframes[i];
    const f2 = keyframes[i + 1];

    const w1 = f1.out;
    const w2 = f2.in;

    const s1 = speeds[i];
    const s2 = speeds[i + 1];

    const bezier = velocityToBezierEase(duration, arcLength, s1, s2, w1, w2);
    const {time, value} = f1;

    return {
      time,
      value,
      ease: 'bezier',
      bezier,
      knots,
    } as Keyframe<T>;
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const {time, value} = keyframes.at(-1)!;

  return [...mapped, {time, value}];
};
